import { chain, maxBy, minBy } from 'lodash';
import dayjs from 'dayjs';
import {
  DefensivoFormValue,
  Propriedade,
  Tratamento,
  TratamentoSementes,
} from '../../@types';
import {
  ConvertParams,
  DataToPrintTS,
  TratamentoSementesToList,
} from './types';
import Agro1Toast from '../../components/Agro1Toast';
import {
  AtividadesService,
  ItensEstoqueService,
  ItensSaldoService,
  PropriedadesService,
  SafrasService,
  UnidadesService,
} from '../../services';
import convertUnidade from '../../utils/convert-unidade';
import {
  getTratamentoIdentification,
  isSegundoTratamento,
} from './create-edit/utils';

export const convertTratamentoSementesToList = (
  data: TratamentoSementes[],
  { atividades, sementesDeOrigem }: ConvertParams,
) => {
  let erroCount = 0;

  const result = data.map<TratamentoSementesToList>((tratamentoSementes) => {
    const atividade = atividades.find(
      (item) => item.id === tratamentoSementes.atividadeId,
    )!;

    const tratamentosParsed = chain(tratamentoSementes.tratamentos)
      .uniqBy('sementeOrigemId')
      .map((item) => {
        const sementeOrigem = sementesDeOrigem.find(
          (semente) => semente.id === item.sementeOrigemId,
        );

        if (!sementeOrigem) {
          erroCount++;
          return 'INSUMO INDISPONÍVEL';
        }

        return sementeOrigem?.descricao;
      })
      .join(', ')
      .value();

    const { dataInicio } = minBy(
      tratamentoSementes.tratamentos,
      (tratamento) => new Date(tratamento.dataInicio),
    )!;

    const { dataFim } = maxBy(
      tratamentoSementes.tratamentos,
      (tratamento) => new Date(tratamento.dataFim),
    )!;

    return {
      ...tratamentoSementes,
      dataInicio,
      dataFim,
      atividade,
      tratamentosParsed,
    };
  });

  if (erroCount) {
    Agro1Toast.show({
      detail: 'Existem tratamentos com insumos inválidos.',
      severity: 'error',
      sticky: true,
    });
  }

  return result;
};

export const getPrintRequiredData = async (
  baseObject: TratamentoSementes,
): Promise<DataToPrintTS> => {
  const propriedade = await PropriedadesService.find(
    baseObject.propriedadeId.toString(),
  );
  const tratamentos = await parseTratamentos(
    baseObject.tratamentos,
    propriedade,
  );

  const safra = await SafrasService.find(baseObject.safraId.toString());
  const atividade = await AtividadesService.find(
    baseObject.atividadeId.toString(),
  );

  return {
    ...baseObject,
    propriedade,
    tratamentosParsed: tratamentos,
    safra,
    atividade,
    dataHoraImpressao: dayjs().format('DD/MM/YYYY HH:mm'),
  };
};

const parseTratamentos = async (
  tratamentos: Tratamento[],
  propriedade: Propriedade,
) => {
  const [listaDefensivos, listaSementesDeOrigem, listaSementesDeDestino] =
    await Promise.all([
      ItensSaldoService.saldos({
        operacaoItem: 'pulverizacao',
        propriedadeId: propriedade.id,
        noPaginate: true,
      }),
      ItensSaldoService.saldos({
        operacaoItem: 'cultivar',
        propriedadeId: propriedade.id,
        sementeValidaTratamento: true,
        noPaginate: true,
      }),
      ItensEstoqueService.estoques({
        operacaoItem: 'cultivar',
        propriedadeId: propriedade.id,
        noPaginate: true,
      }),
    ]);

  const promises = tratamentos.map(async (tratamento) => {
    const sementeOrigem = listaSementesDeOrigem.find(
      (semente) => semente.id === tratamento.sementeOrigemId,
    );

    const sementeDestino = listaSementesDeDestino.find(
      (semente) => semente.itemId === tratamento.sementeDestinoId,
    );

    const defensivos: DefensivoFormValue[] = tratamento.insumos.map((item) => ({
      dose: item.dose,
      quantidade: item.quantidade,
      item: listaDefensivos.find(
        (defensivo) => defensivo.id === item.insumoId,
      )!,
    }));
    const segundoTratamento = isSegundoTratamento({
      sementeOrigem,
      sementeDestino,
    });
    const unidade =
      !segundoTratamento && tratamento.unidadeId
        ? await UnidadesService.find(tratamento.unidadeId.toString())
        : undefined;

    return {
      ...tratamento,
      dataFim: dayjs(tratamento.dataFim).toDate(),
      dataInicio: dayjs(tratamento.dataInicio).toDate(),
      quantidadeTotalSementes: 2,
      defensivos,
      identification: getTratamentoIdentification({
        sementeOrigemId: tratamento.sementeOrigemId,
        sementeDestinoId: tratamento.sementeDestinoId,
        defensivos,
      }),
      search: undefined,
      copiarDeOutraSemente: undefined,
      unidade: unidade ? convertUnidade(unidade) : undefined,
      quantidade:
        tratamento.quantidadeTotalSementes !== undefined && unidade
          ? tratamento.quantidadeTotalSementes / unidade.unidadeConversao.taxa
          : undefined,
      sementeOrigem,
      sementeDestino,
    };
  });

  return Promise.all(promises);
};
